/**
 * Plugin Chain Presets
 * Factory presets and user preset management for master bus chains
 */

// Factory Chain Presets - Pre-configured plugin chains for different use cases
export const FACTORY_CHAIN_PRESETS = {
  // Clean/Transparent
  'transparent-master': {
    id: 'transparent-master',
    name: 'Transparent Master',
    description: 'Clean mastering with subtle enhancement',
    category: 'mastering',
    icon: 'sparkles',
    plugins: [
      {
        type: 'parametric-eq',
        params: {
          lowFreq: 80, lowGain: 1,
          mid1Freq: 300, mid1Gain: -1, mid1Q: 1,
          mid2Freq: 3000, mid2Gain: 1.5, mid2Q: 1,
          highFreq: 10000, highGain: 1.5
        }
      },
      {
        type: 'compressor',
        params: {
          threshold: -18, ratio: 2, attack: 0.02, release: 0.3,
          knee: 10, makeup: 1, mix: 0.5
        }
      }
    ]
  },
  
  // Loud/Modern
  'loud-modern': {
    id: 'loud-modern',
    name: 'Loud & Modern',
    description: 'Punchy, loud master for streaming',
    category: 'mastering',
    icon: 'zap',
    plugins: [
      {
        type: 'parametric-eq',
        params: {
          lowFreq: 60, lowGain: 2,
          mid1Freq: 200, mid1Gain: -2, mid1Q: 1.5,
          mid2Freq: 4000, mid2Gain: 2, mid2Q: 0.8,
          highFreq: 12000, highGain: 2
        }
      },
      {
        type: 'compressor',
        params: {
          threshold: -12, ratio: 4, attack: 0.005, release: 0.15,
          knee: 6, makeup: 4, mix: 1
        }
      },
      {
        type: 'compressor',
        params: {
          threshold: -6, ratio: 10, attack: 0.001, release: 0.05,
          knee: 0, makeup: 2, mix: 1
        }
      }
    ]
  },
  
  // Hip-Hop/Trap
  'hip-hop-master': {
    id: 'hip-hop-master',
    name: 'Hip-Hop Master',
    description: 'Heavy low-end with crisp highs',
    category: 'genre',
    icon: 'music',
    plugins: [
      {
        type: 'parametric-eq',
        params: {
          lowFreq: 50, lowGain: 3,
          mid1Freq: 400, mid1Gain: -2, mid1Q: 2,
          mid2Freq: 2500, mid2Gain: 1, mid2Q: 1,
          highFreq: 8000, highGain: 2.5
        }
      },
      {
        type: 'compressor',
        params: {
          threshold: -15, ratio: 4, attack: 0.01, release: 0.2,
          knee: 6, makeup: 3, mix: 1
        }
      },
      {
        type: 'gain',
        params: { gain: 1, pan: 0, phase: false }
      }
    ]
  },
  
  // EDM/Electronic
  'edm-master': {
    id: 'edm-master',
    name: 'EDM Master',
    description: 'Wide, punchy electronic sound',
    category: 'genre',
    icon: 'radio',
    plugins: [
      {
        type: 'parametric-eq',
        params: {
          lowFreq: 40, lowGain: 2,
          mid1Freq: 250, mid1Gain: -1.5, mid1Q: 1.5,
          mid2Freq: 5000, mid2Gain: 2.5, mid2Q: 0.7,
          highFreq: 14000, highGain: 3
        }
      },
      {
        type: 'compressor',
        params: {
          threshold: -10, ratio: 6, attack: 0.001, release: 0.1,
          knee: 3, makeup: 4, mix: 1
        }
      },
      {
        type: 'chorus',
        params: { rate: 0.2, depth: 0.001, mix: 0.15 }
      }
    ]
  },
  
  // Lo-Fi/Chill
  'lofi-vibes': {
    id: 'lofi-vibes',
    name: 'Lo-Fi Vibes',
    description: 'Warm, vintage lo-fi aesthetic',
    category: 'creative',
    icon: 'coffee',
    plugins: [
      {
        type: 'parametric-eq',
        params: {
          lowFreq: 100, lowGain: 2,
          mid1Freq: 500, mid1Gain: 1, mid1Q: 0.8,
          mid2Freq: 3000, mid2Gain: -2, mid2Q: 1,
          highFreq: 6000, highGain: -4
        }
      },
      {
        type: 'compressor',
        params: {
          threshold: -20, ratio: 3, attack: 0.03, release: 0.4,
          knee: 12, makeup: 2, mix: 0.7
        }
      },
      {
        type: 'chorus',
        params: { rate: 0.3, depth: 0.003, mix: 0.25 }
      },
      {
        type: 'reverb',
        params: { decay: 1.5, preDelay: 0.02, damping: 4000, size: 0.4, mix: 0.2 }
      }
    ]
  },
  
  // Rock/Band
  'rock-master': {
    id: 'rock-master',
    name: 'Rock Master',
    description: 'Powerful, full-range rock sound',
    category: 'genre',
    icon: 'guitar',
    plugins: [
      {
        type: 'parametric-eq',
        params: {
          lowFreq: 80, lowGain: 1.5,
          mid1Freq: 350, mid1Gain: -1, mid1Q: 1.2,
          mid2Freq: 2000, mid2Gain: 1.5, mid2Q: 1,
          highFreq: 8000, highGain: 2
        }
      },
      {
        type: 'compressor',
        params: {
          threshold: -16, ratio: 3, attack: 0.015, release: 0.25,
          knee: 8, makeup: 3, mix: 0.8
        }
      },
      {
        type: 'reverb',
        params: { decay: 0.8, preDelay: 0.01, damping: 6000, size: 0.3, mix: 0.1 }
      }
    ]
  },
  
  // Podcast/Voice
  'podcast-mix': {
    id: 'podcast-mix',
    name: 'Podcast Mix',
    description: 'Clear, broadcast-ready voice',
    category: 'voice',
    icon: 'mic',
    plugins: [
      {
        type: 'parametric-eq',
        params: {
          lowFreq: 120, lowGain: -3,
          mid1Freq: 300, mid1Gain: -1, mid1Q: 1.5,
          mid2Freq: 3500, mid2Gain: 2, mid2Q: 1,
          highFreq: 8000, highGain: 1.5
        }
      },
      {
        type: 'compressor',
        params: {
          threshold: -20, ratio: 4, attack: 0.005, release: 0.2,
          knee: 6, makeup: 4, mix: 1
        }
      },
      {
        type: 'compressor',
        params: {
          threshold: -10, ratio: 8, attack: 0.001, release: 0.1,
          knee: 0, makeup: 2, mix: 1
        }
      }
    ]
  },
  
  // Acoustic/Folk
  'acoustic-warmth': {
    id: 'acoustic-warmth',
    name: 'Acoustic Warmth',
    description: 'Natural, warm acoustic sound',
    category: 'genre',
    icon: 'leaf',
    plugins: [
      {
        type: 'parametric-eq',
        params: {
          lowFreq: 100, lowGain: 1,
          mid1Freq: 400, mid1Gain: 0.5, mid1Q: 0.8,
          mid2Freq: 2500, mid2Gain: 1, mid2Q: 1,
          highFreq: 10000, highGain: 1
        }
      },
      {
        type: 'compressor',
        params: {
          threshold: -22, ratio: 2.5, attack: 0.025, release: 0.35,
          knee: 12, makeup: 2, mix: 0.6
        }
      },
      {
        type: 'reverb',
        params: { decay: 1.2, preDelay: 0.015, damping: 7000, size: 0.5, mix: 0.15 }
      }
    ]
  },
  
  // Cinematic
  'cinematic-epic': {
    id: 'cinematic-epic',
    name: 'Cinematic Epic',
    description: 'Big, wide cinematic sound',
    category: 'creative',
    icon: 'film',
    plugins: [
      {
        type: 'parametric-eq',
        params: {
          lowFreq: 60, lowGain: 2,
          mid1Freq: 200, mid1Gain: -1, mid1Q: 1,
          mid2Freq: 4000, mid2Gain: 1.5, mid2Q: 0.8,
          highFreq: 12000, highGain: 2
        }
      },
      {
        type: 'compressor',
        params: {
          threshold: -18, ratio: 2.5, attack: 0.02, release: 0.3,
          knee: 10, makeup: 2, mix: 0.6
        }
      },
      {
        type: 'reverb',
        params: { decay: 3, preDelay: 0.04, damping: 5000, size: 0.8, mix: 0.25 }
      },
      {
        type: 'delay',
        params: { time: 0.4, feedback: 0.2, stereoSpread: 0.3, filter: 4000, mix: 0.1 }
      }
    ]
  },
  
  // Bright & Airy
  'bright-airy': {
    id: 'bright-airy',
    name: 'Bright & Airy',
    description: 'Open, spacious, bright mix',
    category: 'mastering',
    icon: 'sun',
    plugins: [
      {
        type: 'parametric-eq',
        params: {
          lowFreq: 100, lowGain: 0,
          mid1Freq: 500, mid1Gain: -1.5, mid1Q: 1.2,
          mid2Freq: 4000, mid2Gain: 2.5, mid2Q: 0.8,
          highFreq: 14000, highGain: 3
        }
      },
      {
        type: 'compressor',
        params: {
          threshold: -20, ratio: 2, attack: 0.02, release: 0.3,
          knee: 12, makeup: 1.5, mix: 0.5
        }
      },
      {
        type: 'reverb',
        params: { decay: 1.8, preDelay: 0.025, damping: 8000, size: 0.6, mix: 0.18 }
      }
    ]
  },
  
  // Vintage Tape
  'vintage-tape': {
    id: 'vintage-tape',
    name: 'Vintage Tape',
    description: 'Warm analog tape saturation',
    category: 'creative',
    icon: 'disc',
    plugins: [
      {
        type: 'parametric-eq',
        params: {
          lowFreq: 80, lowGain: 2,
          mid1Freq: 600, mid1Gain: 1, mid1Q: 0.8,
          mid2Freq: 3000, mid2Gain: -1, mid2Q: 1,
          highFreq: 8000, highGain: -2
        }
      },
      {
        type: 'compressor',
        params: {
          threshold: -18, ratio: 3, attack: 0.015, release: 0.25,
          knee: 10, makeup: 3, mix: 0.8
        }
      },
      {
        type: 'chorus',
        params: { rate: 0.15, depth: 0.001, mix: 0.1 }
      }
    ]
  },
  
  // Minimal/Clean
  'minimal-clean': {
    id: 'minimal-clean',
    name: 'Minimal Clean',
    description: 'Simple, clean processing',
    category: 'mastering',
    icon: 'minus',
    plugins: [
      {
        type: 'parametric-eq',
        params: {
          lowFreq: 80, lowGain: 0.5,
          mid1Freq: 300, mid1Gain: 0, mid1Q: 1,
          mid2Freq: 3000, mid2Gain: 0.5, mid2Q: 1,
          highFreq: 10000, highGain: 0.5
        }
      },
      {
        type: 'compressor',
        params: {
          threshold: -24, ratio: 1.5, attack: 0.03, release: 0.4,
          knee: 15, makeup: 1, mix: 0.4
        }
      }
    ]
  }
};

// Preset categories for filtering
export const PRESET_CATEGORIES = {
  all: { name: 'All Presets', icon: 'grid' },
  mastering: { name: 'Mastering', icon: 'gauge' },
  genre: { name: 'Genre', icon: 'music' },
  voice: { name: 'Voice', icon: 'mic' },
  creative: { name: 'Creative', icon: 'sparkles' },
  user: { name: 'My Presets', icon: 'user' }
};

// Local storage key for user presets
const USER_PRESETS_KEY = 'stringsauce_chain_presets';

/**
 * Get user-saved presets from localStorage
 */
export const getUserPresets = () => {
  try {
    const saved = localStorage.getItem(USER_PRESETS_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch (e) {
    console.error('Failed to load user presets:', e);
    return {};
  }
};

/**
 * Save user preset to localStorage
 */
export const saveUserPreset = (preset) => {
  try {
    const userPresets = getUserPresets();
    userPresets[preset.id] = {
      ...preset,
      category: 'user',
      createdAt: new Date().toISOString()
    };
    localStorage.setItem(USER_PRESETS_KEY, JSON.stringify(userPresets));
    return true;
  } catch (e) {
    console.error('Failed to save user preset:', e);
    return false;
  }
};

/**
 * Delete user preset from localStorage
 */
export const deleteUserPreset = (presetId) => {
  try {
    const userPresets = getUserPresets();
    delete userPresets[presetId];
    localStorage.setItem(USER_PRESETS_KEY, JSON.stringify(userPresets));
    return true;
  } catch (e) {
    console.error('Failed to delete user preset:', e);
    return false;
  }
};

/**
 * Get all presets (factory + user)
 */
export const getAllPresets = () => {
  const userPresets = getUserPresets();
  return { ...FACTORY_CHAIN_PRESETS, ...userPresets };
};

/**
 * Serialize current plugin chain to preset format
 */
export const serializeChainToPreset = (plugins, name, description = '') => {
  return {
    id: `user-${Date.now()}`,
    name,
    description,
    category: 'user',
    icon: 'bookmark',
    plugins: plugins.map(plugin => {
      const params = {};
      plugin.parameters?.forEach((param, id) => {
        params[id] = param.getValue ? param.getValue() : param.value;
      });
      
      // Get plugin type from registry name
      const typeName = plugin.name?.toLowerCase().replace(' ', '-') || plugin.type || 'unknown';
      
      return {
        type: typeName === 'parametric eq' ? 'parametric-eq' : typeName,
        params
      };
    })
  };
};

export default {
  FACTORY_CHAIN_PRESETS,
  PRESET_CATEGORIES,
  getUserPresets,
  saveUserPreset,
  deleteUserPreset,
  getAllPresets,
  serializeChainToPreset
};
